import { L_STORAGE_KEYS } from '@src/consts'
import { lStorage } from '@src/utils/'

import { OAuthClientParams } from './type'

export const setOAuthClientParams = (params: OAuthClientParams) => {
  const { clientId, responseType, redirectUrl, state, prompt } = params

  lStorage.removeItem(L_STORAGE_KEYS.UPSIDER_OAUTH_CLIENT_PARAMS)

  if (
    clientId === undefined ||
    responseType === undefined ||
    redirectUrl === undefined ||
    state === undefined
  ) {
    return
  }

  if (responseType !== 'code') {
    return
  }

  lStorage.setItem({
    key: L_STORAGE_KEYS.UPSIDER_OAUTH_CLIENT_PARAMS,
    value: {
      clientId,
      responseType,
      redirectUrl,
      state,
      // Persist only the supported value so the stored shape matches what
      // getOAuthClientParams reads back (defensive against unexpected input).
      ...(prompt === 'login' ? { prompt } : {}),
    },
  })
}

export const isOAuthRequest = (): boolean => {
  return getOAuthClientParams() !== null
}

// Whether the current OAuth request asks for re-authentication (OIDC
// prompt=login). Read from the persisted params instead of the URL query so it
// stays reliable on the MFA pages, which do not carry the query string.
//
// Lifecycle: the flag is written on /signin (setOAuthClientParams) and cleared
// together with the other OAuth params on grant-code success
// (grant-code.tsx) or on the next /signin visit without OAuth params (the
// removeItem at the top of setOAuthClientParams). If the user abandons re-auth
// without hitting either, the flag can linger; it self-heals on the next
// /signin visit, and the MFA pages still require a valid tfaToken to proceed.
export const isOAuthReauthRequired = (): boolean => {
  return getOAuthClientParams()?.prompt === 'login'
}

export const getOAuthClientParams = (): OAuthClientParams | null => {
  const storage = lStorage.getItem(L_STORAGE_KEYS.UPSIDER_OAUTH_CLIENT_PARAMS)
  if (!storage) {
    return null
  }

  try {
    const { clientId, responseType, redirectUrl, state, prompt } =
      JSON.parse(storage)
    const isValid =
      clientId !== undefined &&
      responseType !== undefined &&
      redirectUrl !== undefined &&
      state !== undefined
    if (!isValid) {
      lStorage.removeItem(L_STORAGE_KEYS.UPSIDER_OAUTH_CLIENT_PARAMS)
      return null
    }
    return {
      clientId,
      responseType,
      redirectUrl,
      state,
      ...(prompt === 'login' ? { prompt } : {}),
    }
  } catch (error) {
    lStorage.removeItem(L_STORAGE_KEYS.UPSIDER_OAUTH_CLIENT_PARAMS)
    return null
  }
}
